/**
 * Transporte hacia el backend (Cloudflare Worker + D1).
 *
 * El Worker responde las cabeceras CORS como se debe, incluido el preflight
 * OPTIONS, así que aquí se puede mandar `application/json` normal. (La primera
 * versión de este archivo iba contra Google Apps Script, que no deja poner
 * cabeceras y obligaba a disfrazar el cuerpo como text/plain. Ese truco ya no
 * hace falta y por eso desapareció.)
 *
 * Lo que SÍ se conserva de aquel diseño es que el secreto viaja firmado dentro
 * del cuerpo y no en una cabecera `Authorization`. Sigue siendo mejor: la
 * firma cubre también el contenido, así que nadie puede interceptar la
 * petición y cambiarle el monto sin invalidarla.
 */

const TIEMPO_LIMITE_MS = 15000;

/** Convierte bytes a hexadecimal. */
function aHex(buffer) {
  return [...new Uint8Array(buffer)]
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Firma HMAC-SHA256 del sobre.
 *
 * La cadena que se firma es:  accion|ts|nonce|datos
 * donde `datos` es la CADENA JSON literal, no el objeto. Esto importa: si
 * firmáramos el objeto y el servidor lo re-serializara, cualquier diferencia
 * de orden de claves rompería la firma. JavaScript reordena las claves que
 * parecen enteros al hacer JSON.parse — verificado, pasa de verdad.
 *
 * Comprobado byte por byte contra Utilities.computeHmacSha256Signature de
 * Apps Script, incluyendo tildes, eñes y emoji.
 */
async function firmar(base, secreto) {
  const enc = new TextEncoder();
  const llave = await crypto.subtle.importKey(
    'raw',
    enc.encode(secreto),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign']
  );
  return aHex(await crypto.subtle.sign('HMAC', llave, enc.encode(base)));
}

/** Un identificador de un solo uso, para que una petición no se pueda repetir. */
function nonce() {
  return aHex(crypto.getRandomValues(new Uint8Array(12)));
}

export class ErrorApi extends Error {
  constructor(mensaje, { reintentable = false, causa = null } = {}) {
    super(mensaje);
    this.name = 'ErrorApi';
    this.reintentable = reintentable;
    this.causa = causa;
  }
}

export class Api {
  /**
   * @param {object} cfg
   * @param {string} cfg.url      La URL /exec de la implementación de Apps Script.
   * @param {string} cfg.secreto  El APP_SECRET que generó instalar().
   * @param {number} [cfg.desfaseReloj] Diferencia con el reloj del servidor, en ms.
   */
  constructor({ url, secreto, desfaseReloj = 0 }) {
    this.url = url;
    this.secreto = secreto;
    this.desfaseReloj = desfaseReloj;
  }

  get configurada() {
    return Boolean(this.url && this.secreto);
  }

  /**
   * Envía una acción firmada.
   * @param {string} accion  'ping' | 'sync' | 'ocr'
   * @param {object} datos
   */
  async llamar(accion, datos = {}) {
    if (!this.configurada) {
      throw new ErrorApi('Falta configurar la URL y el secreto del backend.');
    }

    // El reloj del celular puede estar desfasado. El servidor rechaza fuera de
    // ±5 minutos, así que aplicamos la corrección que aprendimos en el ping.
    const ts = Date.now() + this.desfaseReloj;
    const n = nonce();
    const datosStr = JSON.stringify(datos);
    const sig = await firmar(`${accion}|${ts}|${n}|${datosStr}`, this.secreto);

    const control = new AbortController();
    const temporizador = setTimeout(() => control.abort(), TIEMPO_LIMITE_MS);

    let respuesta;
    try {
      respuesta = await fetch(this.url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ accion, ts, nonce: n, sig, datos: datosStr }),
        signal: control.signal,
      });
    } catch (e) {
      // Sin red, DNS caído, o se acabó el tiempo: vale la pena reintentar.
      throw new ErrorApi(
        e.name === 'AbortError' ? 'El servidor tardó demasiado.' : 'Sin conexión.',
        { reintentable: true, causa: e }
      );
    } finally {
      clearTimeout(temporizador);
    }

    const texto = await respuesta.text();
    let cuerpo;
    try {
      cuerpo = JSON.parse(texto);
    } catch {
      throw new ErrorApi(
        `El backend no devolvió JSON (código ${respuesta.status}). ` +
          'Revisa que la URL del Worker sea la correcta.'
      );
    }

    if (!respuesta.ok) {
      // 401 es de firma o de reloj: reintentar no lo arregla, hay que avisar.
      // 5xx suele pasarse solo, así que ese sí vale la pena reintentarlo.
      throw new ErrorApi(cuerpo.error || `El servidor respondió ${respuesta.status}.`, {
        reintentable: respuesta.status >= 500,
      });
    }

    if (!cuerpo.ok) {
      const msg = String(cuerpo.error || 'Error desconocido del backend.');
      // "Servidor ocupado" es el LockService: reintentar tiene sentido.
      throw new ErrorApi(msg, { reintentable: /ocupado|busy/i.test(msg) });
    }

    return cuerpo;
  }

  /**
   * Mide el desfase entre el reloj del dispositivo y el del servidor.
   * Si el celular tiene la hora mal, el servidor rechazaría todo por fuera de
   * la ventana de 5 minutos. Con esto lo compensamos en vez de fallar.
   */
  async sincronizarReloj() {
    const antes = Date.now();
    const r = await this.llamar('ping');
    const despues = Date.now();
    const latenciaMedia = (despues - antes) / 2;
    this.desfaseReloj = Math.round(r.hora + latenciaMedia - despues);
    return this.desfaseReloj;
  }
}
